import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { clientsApi, appointmentsApi, servicesApi } from "../../../services";
import type { Client, Appointment, FollowUpStatusItem } from "../../../types";
import { useCompany } from "../../../contexts/CompanyContext";
import { AdminPageHeaderWithBack } from "../shared/AdminPageHeaderWithBack";
import { Card } from "../../shared/Card";
import { ActionButton } from "../../shared";
import { Icons } from "../../shared/Icons";
import { Loading } from "../../shared/Loading";
import { ErrorState } from "../../shared/ErrorState";
import { EmptyState } from "../../shared/EmptyState";
import { CreateClientModal } from "./CreateClientModal";
import { ClientAppointmentCard } from "./ClientAppointmentCard";
import { CreateAppointmentModal } from "../AdminAppointments/CreateAppointmentModal";
import { UpdatePaymentModal } from "../AdminAppointments/UpdatePaymentModal";
import { CarExpiredFollowUpReminder } from "../AdminAppointments/CarExpiredFollowUpReminder";
import { displayPhoneNumber } from "../../../utils/phoneUtils";
import { maskCpfForDisplay } from "../../../utils/cpfUtils";
import {
  sendWhatsAppMessage,
  getWhatsAppButtonStyles,
  getWhatsAppIconStyles,
  WHATSAPP_ICON_PATH,
} from "../../../utils/whatsappUtils";

export const ClientDetails: React.FC = () => {
  const { clientId } = useParams<{
    clientId: string;
  }>();
  const navigate = useNavigate();
  const { company } = useCompany();
  const [client, setClient] = useState<Client | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditAppointmentModalOpen, setIsEditAppointmentModalOpen] = useState(false);
  const [appointmentToEdit, setAppointmentToEdit] = useState<Appointment | null>(null);
  const [activeTab, setActiveTab] = useState<"future" | "past">("future");
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [appointmentForPayment, setAppointmentForPayment] = useState<Appointment | null>(null);
  const [followUpItems, setFollowUpItems] = useState<FollowUpStatusItem[]>([]);

  const currentClientId = clientId ? parseInt(clientId, 10) : 0;

  const fetchFollowUpStatus = useCallback(async () => {
    try {
      const res = await servicesApi.getFollowUpStatus({
        clientId: currentClientId,
      });
      if (res.success && res.data) {
        setFollowUpItems(res.data.items);
      } else {
        setFollowUpItems([]);
      }
    } catch {
      setFollowUpItems([]);
    }
  }, [currentClientId]);

  const fetchClientDetails = useCallback(async () => {
    try {
      const response = await clientsApi.getClientById(currentClientId);
      if (response.success && response.data) {
        setClient(response.data);
      } else {
        setError("Cliente não encontrado");
      }
    } catch (error) {
      console.error("Error fetching client details:", error);
      setError("Erro ao carregar detalhes do cliente");
    }
  }, [currentClientId]);

  const fetchClientAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await appointmentsApi.getAppointmentsByClientId(
        currentClientId
      );
      if (response.success && response.data) {
        // Sort appointments in descending order by appointment date
        const sortedAppointments = response.data.sort(
          (a, b) =>
            new Date(b.appointmentDate).getTime() -
            new Date(a.appointmentDate).getTime()
        );
        setAppointments(sortedAppointments);
      }
    } catch (error) {
      console.error("Error fetching client appointments:", error);
      setError("Erro ao carregar agendamentos do cliente");
    } finally {
      setLoading(false);
    }
  }, [currentClientId]);

  useEffect(() => {
    if (currentClientId) {
      fetchClientDetails();
      fetchClientAppointments();
      fetchFollowUpStatus();
    }
  }, [currentClientId, fetchClientDetails, fetchClientAppointments, fetchFollowUpStatus]);

  const isAppointmentInPast = (appointmentDate: string, startTime?: string) => {
    const appointmentDateTime = startTime 
      ? new Date(`${appointmentDate}T${startTime}`)
      : new Date(`${appointmentDate}T12:00:00`);
    const now = new Date();
    return appointmentDateTime < now;
  };

  const handleViewAppointment = (appointmentId: number) => {
    navigate(`/admin/appointments/${appointmentId}`);
  };

  const handleViewCars = () => {
    navigate(`/admin/clients/${currentClientId}/cars`);
  };

  const handleEditClient = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    // Refresh client data after update
    if (currentClientId) {
      fetchClientDetails();
    }
  };

  const handleEditAppointment = (appointment: Appointment) => {
    setAppointmentToEdit(appointment);
    setIsEditAppointmentModalOpen(true);
  };

  const handleEditAppointmentClose = () => {
    setIsEditAppointmentModalOpen(false);
    setAppointmentToEdit(null);
  };

  const handleEditAppointmentSuccess = () => {
    setIsEditAppointmentModalOpen(false);
    setAppointmentToEdit(null);
    // Refresh appointments after editing
    fetchClientAppointments();
  };

  // Separate appointments into past and future
  const { pastAppointments, futureAppointments } = useMemo(() => {
    const past: Appointment[] = [];
    const future: Appointment[] = [];

    appointments.forEach((appointment) => {
      if (isAppointmentInPast(appointment.appointmentDate, appointment.startTime)) {
        past.push(appointment);
      } else {
        future.push(appointment);
      }
    });

    // Sort past appointments in descending order (most recent first)
    past.sort(
      (a, b) =>
        new Date(b.appointmentDate).getTime() -
        new Date(a.appointmentDate).getTime()
    );

    // Sort future appointments in ascending order (soonest first)
    future.sort(
      (a, b) =>
        new Date(a.appointmentDate).getTime() -
        new Date(b.appointmentDate).getTime()
    );

    return { pastAppointments: past, futureAppointments: future };
  }, [appointments]);

  if (loading && !client) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <AdminPageHeaderWithBack
          icon={<Icons.Person sx={{ fontSize: 28 }} />}
          title="Detalhes do Cliente"
          subtitle="Carregando..."
          onBackClick={() => navigate(`/admin/clients`)}
        />
        <Loading message="Carregando detalhes do cliente..." size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <AdminPageHeaderWithBack
          icon={<Icons.Person sx={{ fontSize: 28 }} />}
          title="Detalhes do Cliente"
          subtitle="Erro ao carregar"
          onBackClick={() =>
            navigate(-1)
          }
        />
        <ErrorState
          message={error}
          onRetry={() => {
            fetchClientDetails();
            fetchClientAppointments();
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      <AdminPageHeaderWithBack
        icon={<Icons.Person sx={{ fontSize: 28 }} />}
        title={client?.name || "Cliente"}
        subtitle="Detalhes do cliente e histórico de agendamentos"
        onBackClick={() =>
          navigate(-1)
        }
      />

      <Card className="p-4 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">
                {client?.name}
              </h2>
              {client ? (
                <CarExpiredFollowUpReminder
                  items={followUpItems}
                  clientId={client.id}
                  clientName={client.name}
                  clientPhone={client.phoneNumber}
                  onScheduled={fetchFollowUpStatus}
                />
              ) : null}
            </div>
            <div className="mt-3 space-y-2 text-gray-600">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <Icons.Phone sx={{ fontSize: 20, color: "#3b82f6" }} />
                  <span className="text-sm sm:text-base break-all">
                    {displayPhoneNumber(client?.phoneNumber || "")}
                  </span>
                </div>
                {client?.phoneNumber && (
                  <button
                    onClick={() =>
                      sendWhatsAppMessage({
                        phoneNumber: client.phoneNumber,
                        message: "",
                      })
                    }
                    className={getWhatsAppButtonStyles("sm")}
                    title="Enviar mensagem via WhatsApp"
                  >
                    <svg
                      className={getWhatsAppIconStyles("sm")}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d={WHATSAPP_ICON_PATH} />
                    </svg>
                  </button>
                )}
              </div>
              {client?.address && (
                <div className="flex items-start gap-2">
                  <span className="text-blue-500 shrink-0">📍</span>
                  <span className="text-sm sm:text-base break-words">
                    {client.address}
                  </span>
                </div>
              )}
              {client?.cpf && (
                <div className="flex items-center gap-2">
                  <Icons.Badge
                    sx={{ fontSize: 20, color: "#3b82f6" }}
                    titleAccess="CPF"
                  />
                  <span className="text-sm sm:text-base text-gray-600 tracking-wide">
                    {maskCpfForDisplay(client.cpf)}
                  </span>
                </div>
              )}
              <p className="text-xs sm:text-sm text-gray-500 pt-1">
                Cliente desde:{" "}
                {client?.createdAt
                  ? new Date(client?.createdAt).toLocaleDateString("pt-BR", {
                      month: "long",
                      year: "numeric",
                    })
                  : ""}
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto shrink-0">
            <ActionButton
              onClick={handleEditClient}
              variant="outline"
              size="sm"
              icon={<Icons.Edit sx={{ fontSize: 18 }} />}
              className="w-full sm:w-auto justify-center"
            >
              Editar
            </ActionButton>
            {company?.category === "car" && (
              <ActionButton
                onClick={handleViewCars}
                variant="primary"
                size="sm"
                icon={<Icons.Car sx={{ fontSize: 18 }} />}
                className="w-full sm:w-auto justify-center"
              >
                Veículos
              </ActionButton>
            )}
          </div>
        </div>
      </Card>

      <div className="space-y-4 sm:space-y-6">
        {loading ? (
          <Card className="p-4 sm:p-6">
            <Loading message="Carregando agendamentos..." />
          </Card>
        ) : appointments.length === 0 ? (
          <Card className="p-4 sm:p-6">
            <EmptyState
              icon={<Icons.Calendar sx={{ fontSize: 80 }} />}
              title="Nenhum agendamento encontrado"
              description="Este cliente ainda não possui agendamentos."
            />
          </Card>
        ) : (
          <>
            <div className="bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 p-1.5">
              <nav className="grid grid-cols-2 gap-1">
                <button
                  type="button"
                  onClick={() => setActiveTab("future")}
                  className={`flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                    activeTab === "future"
                      ? "bg-sky-50 dark:bg-sky-950 text-sky-700 dark:text-sky-300 shadow-sm"
                      : "text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-700"
                  }`}
                >
                  <Icons.Calendar sx={{ fontSize: 18 }} />
                  <span className="truncate">
                    <span className="sm:hidden">Próximos</span>
                    <span className="hidden sm:inline">Próximos agendamentos</span>
                  </span>
                  {futureAppointments.length > 0 && (
                    <span className="text-[11px] sm:text-xs bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded-full font-semibold">
                      {futureAppointments.length}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("past")}
                  className={`flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                    activeTab === "past"
                      ? "bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm"
                      : "text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-700"
                  }`}
                >
                  <Icons.History sx={{ fontSize: 18 }} />
                  <span className="truncate">
                    <span className="sm:hidden">Anteriores</span>
                    <span className="hidden sm:inline">Agendamentos anteriores</span>
                  </span>
                  {pastAppointments.length > 0 && (
                    <span className="text-[11px] sm:text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full font-semibold">
                      {pastAppointments.length}
                    </span>
                  )}
                </button>
              </nav>
            </div>

            {activeTab === "future" ? (
              futureAppointments.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                  {futureAppointments.map((appointment) => (
                    <ClientAppointmentCard
                      key={appointment.id}
                      appointment={appointment}
                      company={company}
                      onView={handleViewAppointment}
                      onEdit={handleEditAppointment}
                      onPay={(item) => {
                        setAppointmentForPayment(item);
                        setPaymentModalOpen(true);
                      }}
                    />
                  ))}
                </div>
              ) : (
                <Card className="p-4 sm:p-6">
                  <EmptyState
                    icon={<Icons.Calendar sx={{ fontSize: 80 }} />}
                    title="Nenhum agendamento futuro"
                    description="Este cliente não possui agendamentos futuros."
                  />
                </Card>
              )
            ) : pastAppointments.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                {pastAppointments.map((appointment) => (
                    <ClientAppointmentCard
                      key={appointment.id}
                      appointment={appointment}
                      company={company}
                      isPast
                      onView={handleViewAppointment}
                      onEdit={handleEditAppointment}
                      onPay={(item) => {
                        setAppointmentForPayment(item);
                        setPaymentModalOpen(true);
                      }}
                    />
                ))}
              </div>
            ) : (
              <Card className="p-4 sm:p-6">
                <EmptyState
                  icon={<Icons.History sx={{ fontSize: 80 }} />}
                  title="Nenhum agendamento anterior"
                  description="Este cliente não possui agendamentos anteriores."
                />
              </Card>
            )}
          </>
        )}
      </div>

      {/* Edit Client Modal */}
      <CreateClientModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        client={client}
      />

      {/* Edit Appointment Modal */}
      {appointmentToEdit && (
        <CreateAppointmentModal
          isOpen={isEditAppointmentModalOpen}
          onClose={handleEditAppointmentClose}
          onSuccess={handleEditAppointmentSuccess}
          editingAppointment={{
            id: appointmentToEdit.id,
            clientId: appointmentToEdit.clientId,
            serviceId: appointmentToEdit.serviceId,
            professionalId: appointmentToEdit.professionalId,
            appointmentDate: appointmentToEdit.appointmentDate,
            startTime: appointmentToEdit.startTime,
            price: appointmentToEdit.price,
            clientCarId: appointmentToEdit.clientCarId,
          }}
          selectedDate={appointmentToEdit.appointmentDate}
        />
      )}

      {/* Payment Modal */}
      {paymentModalOpen && appointmentForPayment && (
        <UpdatePaymentModal
          isOpen={paymentModalOpen}
          onClose={() => {
            setPaymentModalOpen(false);
            setAppointmentForPayment(null);
          }}
          onSuccess={(updatedAppointment) => {
            if (updatedAppointment) {
              // Update the appointment directly in state
              setAppointments((prev) =>
                prev.map((apt) =>
                  apt.id === updatedAppointment.id ? updatedAppointment : apt
                )
              );
            }
            setPaymentModalOpen(false);
            setAppointmentForPayment(null);
          }}
          appointment={appointmentForPayment}
        />
      )}
    </div>
  );
};
